import { BaseApiService } from './base.service';

export interface PDFTemplate {
  id: number;  // Changed from template_id: string to match backend
  template_id?: string;  // Keep for backward compatibility
  template_name: string;
  document_type: 'invoice' | 'invoice_draft' | 'payment_confirmation' | 'reminder_no_fee' | 'reminder_with_fee';
  language: 'de' | 'en' | 'pt-br';
  template_path: string;
  template_filename: string;
  is_default: boolean;
  is_active: boolean;
  version: string;
  description?: string;
  notes?: string;
  created_at: string;
  created_by: string;
  updated_at?: string;
  updated_by?: string;
}

export interface TemplateListResponse {
  templates: PDFTemplate[];
  total: number;
  page: number;
  page_size: number;
}

export interface TemplateTranslation {
  translation_id: string;
  template_id: string;
  translation_key: string;
  translation_value: string;
  language: 'de' | 'en' | 'pt-br';
  context?: string;
}

export interface CreateTranslationPayload {
  translation_key: string;
  translation_value: string;
  language: 'de' | 'en' | 'pt-br';
  context?: string;
}

export interface UpdateTranslationPayload extends Partial<CreateTranslationPayload> {}

export interface CreateTemplatePayload {
  template_name: string;
  document_type: PDFTemplate['document_type'];
  language: PDFTemplate['language'];
  template_path: string;
  template_filename: string;
  is_default?: boolean;
  is_active?: boolean;
  version?: string;
  description?: string;
  notes?: string;
  created_by: string;
}

export interface UpdateTemplatePayload extends Partial<CreateTemplatePayload> {
  updated_by: string;
}

export class PDFTemplateService extends BaseApiService {
  private readonly BASE_ENDPOINT = "/api/v1/documents/templates";

  async listTemplates(
    params: {
      document_type?: PDFTemplate['document_type'];
      language?: PDFTemplate['language'];
      is_active?: boolean;
      is_default?: boolean;
      page?: number;
      page_size?: number;
    } = {}
  ): Promise<TemplateListResponse> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        query.append(key, String(value));
      }
    });

    const url = query.toString()
      ? `${this.BASE_ENDPOINT}?${query.toString()}`
      : this.BASE_ENDPOINT;
    return await this.get<TemplateListResponse>(url);
  }

  async createTemplate(data: CreateTemplatePayload): Promise<PDFTemplate> {
    return await this.post<PDFTemplate, CreateTemplatePayload>(this.BASE_ENDPOINT, data);
  }

  async updateTemplate(templateId: number | string, data: UpdateTemplatePayload): Promise<PDFTemplate> {
    return await this.put<PDFTemplate, UpdateTemplatePayload>(`${this.BASE_ENDPOINT}/${templateId}`, data);
  }

  async deleteTemplate(templateId: number | string): Promise<{ message: string }> {
    return await this.delete<{ message: string }>(`${this.BASE_ENDPOINT}/${templateId}`);
  }

  // ----- Translation Management -----
  async listTranslations(templateId: string): Promise<TemplateTranslation[]> {
    return await this.get<TemplateTranslation[]>(`${this.BASE_ENDPOINT}/${templateId}/translations`);
  }

  async createTranslation(templateId: string, data: CreateTranslationPayload): Promise<TemplateTranslation> {
    return await this.post<TemplateTranslation, CreateTranslationPayload>(`${this.BASE_ENDPOINT}/${templateId}/translations`, data);
  }

  async updateTranslation(translationId: string, data: UpdateTranslationPayload): Promise<TemplateTranslation> {
    return await this.put<TemplateTranslation, UpdateTranslationPayload>(`${this.BASE_ENDPOINT}/translations/${translationId}`, data);
  }

  async deleteTranslation(translationId: string): Promise<{ message: string }> {
    return await this.delete<{ message: string }>(`${this.BASE_ENDPOINT}/translations/${translationId}`);
  }

  // ----- Upload template file -----
  async uploadTemplateFile(templateId: number | string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);

    // We need to use axios instance directly because of multipart/form-data
    const response = await this.axiosInstance.post(
      `${this.BASE_ENDPOINT}/${templateId}/upload`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  }

  // ----- Import/Export templates -----
  async importTemplates(file: File, companyId?: number, overrideExisting: boolean = false): Promise<{
    success: boolean;
    message: string;
    results: {
      imported: Array<{ template_name: string; action: string; template_id?: string }>;
      skipped: Array<{ template_name: string; reason: string }>;
      errors: Array<{ index: number; template_name: string; error: string }>;
      total: number;
    };
  }> {
    const formData = new FormData();
    formData.append('file', file);
    if (companyId !== undefined) {
      formData.append('company_id', companyId.toString());
    }
    formData.append('override_existing', overrideExisting.toString());

    const response = await this.axiosInstance.post(
      '/api/v1/templates/import',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    );
    return response.data;
  }

  async importDefaultTemplates(companyId?: number, overrideExisting: boolean = false): Promise<{
    success: boolean;
    message: string;
    imported_count: number;
    errors?: string[];
  }> {
    return await this.post('/api/v1/templates/import/default', {
      company_id: companyId,
      override_existing: overrideExisting
    });
  }

  async exportTemplates(companyId?: number, includeContent: boolean = false): Promise<Array<{
    template_name: string;
    document_type: string;
    language: string;
    template_path: string;
    template_filename: string;
    description?: string;
    is_default: boolean;
    is_active: boolean;
    template_content?: string;
  }>> {
    const params = new URLSearchParams();
    if (companyId !== undefined) {
      params.append('company_id', companyId.toString());
    }
    params.append('include_content', includeContent.toString());

    return await this.get(`/api/v1/templates/export?${params.toString()}`);
  }
}
